import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";

import {
  deletePreferences,
  putPreferences,
} from "../api/preferences";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";
import { trapTabKey } from "../../tasksShared/lib/focusTrap";

// ClientPreferencesEditModal — feature-local criteria editor
// (CRM-CLIENT-CONTACTS-MATCHING-A1). Controlled open/close from parent
// via ClientPreferencesSection. PUT upsert; optional DELETE clear.

const DEAL_TYPE_OPTIONS = [
  { value: "", label: "— не обрано —" },
  { value: "sale", label: "Продаж" },
  { value: "rent", label: "Оренда" },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: "", label: "— не обрано —" },
  { value: "apartment", label: "Квартира" },
  { value: "house", label: "Будинок" },
  { value: "land", label: "Земля" },
  { value: "commercial", label: "Комерція" },
  { value: "industrial", label: "Промислова" },
];

const EDITABLE_KEYS = [
  "deal_type",
  "property_type",
  "min_price",
  "max_price",
  "min_area",
  "max_area",
  "rooms_min",
  "rooms_max",
  "city",
  "district",
];

const EMPTY_DRAFT = {
  deal_type: "",
  property_type: "",
  min_price: "",
  max_price: "",
  min_area: "",
  max_area: "",
  rooms_min: "",
  rooms_max: "",
  city: "",
  district: "",
};

const toDraft = (prefs) => {
  if (!prefs) return { ...EMPTY_DRAFT };
  return {
    deal_type: prefs.deal_type || "",
    property_type: prefs.property_type || "",
    min_price:
      prefs.min_price === null || prefs.min_price === undefined
        ? ""
        : String(prefs.min_price),
    max_price:
      prefs.max_price === null || prefs.max_price === undefined
        ? ""
        : String(prefs.max_price),
    min_area:
      prefs.min_area === null || prefs.min_area === undefined
        ? ""
        : String(prefs.min_area),
    max_area:
      prefs.max_area === null || prefs.max_area === undefined
        ? ""
        : String(prefs.max_area),
    rooms_min:
      prefs.rooms_min === null || prefs.rooms_min === undefined
        ? ""
        : String(prefs.rooms_min),
    rooms_max:
      prefs.rooms_max === null || prefs.rooms_max === undefined
        ? ""
        : String(prefs.rooms_max),
    city: prefs.city || "",
    district: prefs.district || "",
  };
};

const parseOptionalNumber = (raw) => {
  if (raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return NaN;
  return n;
};

const buildPayload = (draft) => ({
  deal_type: draft.deal_type || "",
  property_type: draft.property_type || "",
  min_price: parseOptionalNumber(draft.min_price),
  max_price: parseOptionalNumber(draft.max_price),
  min_area: parseOptionalNumber(draft.min_area),
  max_area: parseOptionalNumber(draft.max_area),
  rooms_min: parseOptionalNumber(draft.rooms_min),
  rooms_max: parseOptionalNumber(draft.rooms_max),
  city: (draft.city || "").trim(),
  district: (draft.district || "").trim(),
});

const isPayloadEmpty = (payload) => {
  if (payload.deal_type) return false;
  if (payload.property_type) return false;
  if (payload.min_price !== null) return false;
  if (payload.max_price !== null) return false;
  if (payload.min_area !== null) return false;
  if (payload.max_area !== null) return false;
  if (payload.rooms_min !== null) return false;
  if (payload.rooms_max !== null) return false;
  if (payload.city) return false;
  if (payload.district) return false;
  return true;
};

const validateClientSide = (payload) => {
  const errors = {};

  const checkNonNegative = (field, label) => {
    const value = payload[field];
    if (value === null) return;
    if (Number.isNaN(value)) {
      errors[field] = `${label}: вкажіть коректне число.`;
      return;
    }
    if (value < 0) {
      errors[field] = `${label} не може бути від'ємним.`;
    }
  };

  checkNonNegative("min_price", "Мінімальна ціна");
  checkNonNegative("max_price", "Максимальна ціна");
  checkNonNegative("min_area", "Мінімальна площа");
  checkNonNegative("max_area", "Максимальна площа");
  checkNonNegative("rooms_min", "Мінімальна кількість кімнат");
  checkNonNegative("rooms_max", "Максимальна кількість кімнат");

  if (
    payload.min_price !== null &&
    payload.max_price !== null &&
    !Number.isNaN(payload.min_price) &&
    !Number.isNaN(payload.max_price) &&
    payload.min_price > payload.max_price
  ) {
    errors.min_price = "Мінімальна ціна не може перевищувати максимальну.";
    errors.max_price = errors.max_price || errors.min_price;
  }

  if (
    payload.min_area !== null &&
    payload.max_area !== null &&
    !Number.isNaN(payload.min_area) &&
    !Number.isNaN(payload.max_area) &&
    payload.min_area > payload.max_area
  ) {
    errors.min_area = "Мінімальна площа не може перевищувати максимальну.";
    errors.max_area = errors.max_area || errors.min_area;
  }

  if (
    payload.rooms_min !== null &&
    payload.rooms_max !== null &&
    !Number.isNaN(payload.rooms_min) &&
    !Number.isNaN(payload.rooms_max) &&
    payload.rooms_min > payload.rooms_max
  ) {
    errors.rooms_min =
      "Мінімальна кількість кімнат не може перевищувати максимальну.";
    errors.rooms_max = errors.rooms_max || errors.rooms_min;
  }

  return errors;
};

function ClientPreferencesEditModal({
  open,
  onClose,
  clientId,
  preferences,
  onSaved,
}) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitState, setSubmitState] = useState("idle");
  const [clearing, setClearing] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const submittingRef = useRef(false);
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const hasExisting = Boolean(preferences);

  useEffect(() => {
    if (open) {
      setDraft(toDraft(preferences));
      setFieldErrors({});
      setFormError(null);
      setSubmitState("idle");
      setClearing(false);
      setClearConfirmOpen(false);
      submittingRef.current = false;
    }
  }, [open, preferences]);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocusedRef.current = document.activeElement;
    const focusTimer = window.setTimeout(() => {
      const firstField = dialogRef.current?.querySelector(
        "select, input, textarea, button:not([disabled])",
      );
      if (firstField && typeof firstField.focus === "function") {
        firstField.focus();
      } else if (dialogRef.current && typeof dialogRef.current.focus === "function") {
        dialogRef.current.focus();
      }
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (submittingRef.current) return;
        onClose();
        return;
      }
      trapTabKey(event, dialogRef.current);
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (
        previouslyFocusedRef.current &&
        typeof previouslyFocusedRef.current.focus === "function"
      ) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const submitting = submitState === "submitting" || clearing;

  const handleFieldChange = (field) => (event) => {
    const next = event.target.value;
    setDraft((prev) => ({ ...prev, [field]: next }));
    setFieldErrors((prev) => {
      if (!prev[field] && !prev._form) return prev;
      const out = { ...prev };
      delete out[field];
      delete out._form;
      return out;
    });
    setFormError(null);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submittingRef.current) return;

    const payload = buildPayload(draft);
    const clientErrors = validateClientSide(payload);
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      setFormError("Виправте помилки у формі.");
      return;
    }

    if (isPayloadEmpty(payload) && hasExisting) {
      setClearConfirmOpen(true);
      return;
    }

    if (isPayloadEmpty(payload) && !hasExisting) {
      setFormError("Вкажіть хоча б один критерій пошуку.");
      return;
    }

    // Normalize NaN-guarded numbers for API (null for empty).
    const body = { ...payload };
    EDITABLE_KEYS.forEach((key) => {
      if (
        key.endsWith("_price") ||
        key.endsWith("_area") ||
        key.startsWith("rooms_")
      ) {
        if (body[key] !== null && Number.isNaN(body[key])) {
          body[key] = null;
        }
      }
    });

    submittingRef.current = true;
    setSubmitState("submitting");
    setFieldErrors({});
    setFormError(null);

    try {
      const saved = await putPreferences(clientId, body);
      setSubmitState("saved");
      toast.success("Критерії збережено.");
      if (typeof onSaved === "function") {
        onSaved(saved);
      }
    } catch (err) {
      const parsed = parseDrfFieldErrors(err, EDITABLE_KEYS);
      setFieldErrors(parsed);
      setFormError(parsed._form || messageFromError(err));
      setSubmitState("error");
    } finally {
      submittingRef.current = false;
    }
  };

  const handleClearRequest = () => {
    if (!hasExisting || submittingRef.current) return;
    setClearConfirmOpen(true);
    setFormError(null);
  };

  const handleClearCancel = () => {
    if (clearing) return;
    setClearConfirmOpen(false);
  };

  const handleClearConfirm = async () => {
    if (!hasExisting || submittingRef.current) return;
    submittingRef.current = true;
    setClearConfirmOpen(false);
    setClearing(true);
    setFieldErrors({});
    setFormError(null);
    try {
      await deletePreferences(clientId);
      toast.success("Критерії очищено.");
      if (typeof onSaved === "function") {
        onSaved(null);
      }
    } catch (err) {
      setFormError(messageFromError(err));
    } finally {
      submittingRef.current = false;
      setClearing(false);
    }
  };

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-preferences-edit-modal-title"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={() => {
          if (!submitting) onClose();
        }}
      />
      <div
        ref={dialogRef}
        className="feature-client-detail__modal-dialog feature-client-detail__modal-dialog--prefs"
        tabIndex={-1}
      >
        <header className="feature-client-detail__modal-head">
          <h2
            id="client-preferences-edit-modal-title"
            className="feature-client-detail__modal-title"
          >
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <span>Критерії пошуку</span>
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Закрити вікно критеріїв"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          <form className="ui-form" onSubmit={handleSubmit} noValidate>
            <div className="ui-form__rows feature-client-detail__prefs-form-rows">
              <Field
                id="client-prefs-deal-type"
                label="Тип угоди"
                error={fieldErrors.deal_type}
              >
                <Select
                  name="deal_type"
                  value={draft.deal_type}
                  onChange={handleFieldChange("deal_type")}
                  options={DEAL_TYPE_OPTIONS}
                  disabled={submitting}
                />
              </Field>

              <Field
                id="client-prefs-property-type"
                label="Тип нерухомості"
                error={fieldErrors.property_type}
              >
                <Select
                  name="property_type"
                  value={draft.property_type}
                  onChange={handleFieldChange("property_type")}
                  options={PROPERTY_TYPE_OPTIONS}
                  disabled={submitting}
                />
              </Field>

              <div className="feature-client-detail__prefs-range">
                <Field
                  id="client-prefs-min-price"
                  label="Ціна від"
                  error={fieldErrors.min_price}
                >
                  <TextInput
                    name="min_price"
                    type="number"
                    min="0"
                    step="any"
                    value={draft.min_price}
                    onChange={handleFieldChange("min_price")}
                    disabled={submitting}
                  />
                </Field>
                <Field
                  id="client-prefs-max-price"
                  label="Ціна до"
                  error={fieldErrors.max_price}
                >
                  <TextInput
                    name="max_price"
                    type="number"
                    min="0"
                    step="any"
                    value={draft.max_price}
                    onChange={handleFieldChange("max_price")}
                    disabled={submitting}
                  />
                </Field>
              </div>

              <div className="feature-client-detail__prefs-range">
                <Field
                  id="client-prefs-min-area"
                  label="Площа від, м²"
                  error={fieldErrors.min_area}
                >
                  <TextInput
                    name="min_area"
                    type="number"
                    min="0"
                    step="any"
                    value={draft.min_area}
                    onChange={handleFieldChange("min_area")}
                    disabled={submitting}
                  />
                </Field>
                <Field
                  id="client-prefs-max-area"
                  label="Площа до, м²"
                  error={fieldErrors.max_area}
                >
                  <TextInput
                    name="max_area"
                    type="number"
                    min="0"
                    step="any"
                    value={draft.max_area}
                    onChange={handleFieldChange("max_area")}
                    disabled={submitting}
                  />
                </Field>
              </div>

              <div className="feature-client-detail__prefs-range">
                <Field
                  id="client-prefs-rooms-min"
                  label="Кімнат від"
                  error={fieldErrors.rooms_min}
                >
                  <TextInput
                    name="rooms_min"
                    type="number"
                    min="0"
                    step="1"
                    value={draft.rooms_min}
                    onChange={handleFieldChange("rooms_min")}
                    disabled={submitting}
                  />
                </Field>
                <Field
                  id="client-prefs-rooms-max"
                  label="Кімнат до"
                  error={fieldErrors.rooms_max}
                >
                  <TextInput
                    name="rooms_max"
                    type="number"
                    min="0"
                    step="1"
                    value={draft.rooms_max}
                    onChange={handleFieldChange("rooms_max")}
                    disabled={submitting}
                  />
                </Field>
              </div>

              <Field
                id="client-prefs-city"
                label="Місто"
                error={fieldErrors.city}
              >
                <TextInput
                  name="city"
                  value={draft.city}
                  onChange={handleFieldChange("city")}
                  maxLength={120}
                  disabled={submitting}
                />
              </Field>

              <Field
                id="client-prefs-district"
                label="Район"
                error={fieldErrors.district}
              >
                <TextInput
                  name="district"
                  value={draft.district}
                  onChange={handleFieldChange("district")}
                  maxLength={120}
                  disabled={submitting}
                />
              </Field>
            </div>

            <div className="ui-form__messages">
              {formError ? (
                <InlineError className="ui-form__message">
                  {formError}
                </InlineError>
              ) : null}
            </div>

            <div className="ui-form__actions feature-client-detail__prefs-form-actions">
              {hasExisting ? (
                <button
                  type="button"
                  className="ui-form__button feature-client-detail__prefs-clear-btn"
                  onClick={handleClearRequest}
                  disabled={submitting}
                  aria-expanded={clearConfirmOpen}
                  data-testid="client-prefs-clear-action"
                >
                  {clearing ? (
                    <Spinner size="sm" label="Очищення" />
                  ) : (
                    "Очистити критерії"
                  )}
                </button>
              ) : null}
              <button
                type="button"
                className="ui-form__button ui-form__button--ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Скасувати
              </button>
              <button
                type="submit"
                className="ui-form__button ui-form__button--primary"
                disabled={submitting}
                data-loading={submitState === "submitting" ? "true" : undefined}
              >
                {submitState === "submitting" ? (
                  <Spinner size="sm" label="Збереження" />
                ) : (
                  "Зберегти"
                )}
              </button>
            </div>

            {clearConfirmOpen ? (
              <div
                className="feature-client-detail__prefs-clear-confirm"
                role="alertdialog"
                aria-labelledby="client-prefs-clear-title"
                aria-describedby="client-prefs-clear-desc"
                data-testid="client-prefs-clear-confirm"
              >
                <p id="client-prefs-clear-title" className="feature-client-detail__prefs-clear-title">
                  Очистити критерії?
                </p>
                <p id="client-prefs-clear-desc" className="feature-client-detail__prefs-clear-desc">
                  Усі критерії пошуку буде видалено. Цю дію не можна скасувати.
                </p>
                <div className="feature-client-detail__prefs-clear-actions">
                  <button
                    type="button"
                    className="ui-form__button ui-form__button--ghost"
                    onClick={handleClearCancel}
                    disabled={clearing}
                  >
                    Скасувати
                  </button>
                  <button
                    type="button"
                    className="ui-form__button feature-client-detail__prefs-clear-btn"
                    onClick={() => void handleClearConfirm()}
                    disabled={clearing}
                    data-testid="client-prefs-clear-confirm-action"
                  >
                    {clearing ? (
                      <Spinner size="sm" label="Очищення" />
                    ) : (
                      "Очистити"
                    )}
                  </button>
                </div>
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </div>
  );
}

export default ClientPreferencesEditModal;
